import { Component, OnInit, effect, inject, input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MemberStatuses, RoleIds } from '../../../../core/enums/data.enums';
import { MemberPermission } from '../../../../core/enums/permissions.enums';
import { MultiSelectComponent } from "../../../../parts/multi-select/multi-select.component";
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { UserSessionService } from '../../../../core/services/user-session.service';
import { ListConstants } from '../../../../core/constants/lists.constants';
import { AuthorizationService } from '../../../../core/services/authorization/authorization.service';
import { AuthorizationStateService } from '../../../../core/services/authorization/authorization-state.service';
import { ProfileOrganizationScopeService } from '../../../../core/services/authorization/profile-organization-scope.service';
import { PermissionUtilities } from '../../../../core/utilities/permission.utilities';
import { ParentFocusDirective } from "../../../../core/directives/parent-focus.directive";
import { MultiSelectLabelText } from '../../../../core/enums/forms.enums';
import { LocalStorageKeyConstants } from '../../../../core/constants/constants';
import { MembersFilter } from '../../../../core/filters/members.filter';
import { BaseSearchFilterComponent } from '../../../../core/base-classes/base-search-filter.component';

@Component({
  selector: 'ms-member-search-filter',
  imports: [CommonModule, FormsModule, MultiSelectComponent, FontAwesomeModule, ParentFocusDirective],
  templateUrl: './member-search-filter.component.html'
})
export class MemberSearchFilterComponent extends BaseSearchFilterComponent<MembersFilter> implements OnInit {

  // When provided, the filter operates on this isolated filter instead of the shared session filter — so an
  // embedding component (e.g. the email recipient selector) can filter without disturbing the main member list.
  public localFilter = input<MembersFilter | undefined>(undefined);
  public localStorageKey = input<string | undefined>(undefined);
  // Lets an embedder reset to ITS defaults rather than the Members page's (DEV-2694).
  public defaultFilterFactory = input<(() => MembersFilter) | undefined>(undefined);

  public MemberStatuses = MemberStatuses;
  public MultiSelectLabelText = MultiSelectLabelText;

  // Canonical filter options per DEV-2505. Suspended is intentionally excluded from the
  // dropdown even though the entity can still carry it (legacy DEV data). Deleted is
  // appended by applyDeletedMemberAccess() when the acting user has ReadDeleted.
  private static readonly BASE_STATUS_OPTIONS: MemberStatuses[] = [
    MemberStatuses.Created,
    MemberStatuses.Invited,
    MemberStatuses.Expired,
    MemberStatuses.Active,
    MemberStatuses.Deactivated
  ];
  public selectMemberStatusOptions: MemberStatuses[] = [...MemberSearchFilterComponent.BASE_STATUS_OPTIONS];
  public canViewDeletedMembers = false;

  // Dropdown options are the RoleIds enum KEYS (ms-multi-select's native <select> coerces values
  // to strings, so numeric ids can't be used directly); onRoleChange maps them back to ids.
  public selectRoleOptions: (keyof typeof RoleIds)[] = ['Member', 'Administrator', 'Owner'];
  public selectedRoleOptions = signal<(keyof typeof RoleIds)[]>([]);

  // Input field values - these are bound to the actual input fields
  public firstNameInput = '';
  public lastNameInput = '';
  public emailInput = '';
  public phoneInput = '';

  public selectedMemberStatusOptions = signal<MemberStatuses[]>([]);

  private authorizationService = inject(AuthorizationService);
  private authorizationStateService = inject(AuthorizationStateService);
  // Provided by an org-scoped profile page — this filter renders inside the member lists on the
  // organization profile (manage admins, transfer ownership) and the property profile (attach
  // members) — so read_deleted is gated on the organization on screen rather than the active one.
  // Null on the organization-wide Members page, which keeps the active organization (DEV-2643).
  private profileScope = inject(ProfileOrganizationScopeService, { optional: true });

  constructor(public userSessionService: UserSessionService) {
    super();
    // Reactively re-evaluate deleted-member access when auth state or the scoped organization
    // changes, so the option doesn't go stale on org switch or once the auth state finishes loading.
    effect(() => {
      const authState = this.authorizationStateService.authorizationState();
      this.applyDeletedMemberAccess(authState ?? null);
    });
  }

  private applyDeletedMemberAccess(
    authState: ReturnType<AuthorizationStateService['authorizationState']> | null
  ): void {
    this.canViewDeletedMembers = authState
      ? PermissionUtilities.hasScopedOrganizationPermission(
          MemberPermission.ReadDeleted, authState, this.authorizationService,
          this.profileScope, this.userSessionService)
      : false;

    if (this.canViewDeletedMembers) {
      this.selectMemberStatusOptions = [...MemberSearchFilterComponent.BASE_STATUS_OPTIONS, MemberStatuses.Deleted];
      this.activeFilter.includeDeletedMembers = true;
    } else {
      this.selectMemberStatusOptions = [...MemberSearchFilterComponent.BASE_STATUS_OPTIONS];
      this.activeFilter.includeDeletedMembers = false;

      // Remove Deleted from any persisted filter statuses
      if (this.activeFilter.statuses?.includes(MemberStatuses.Deleted)) {
        this.activeFilter.statuses = this.activeFilter.statuses.filter(s => s !== MemberStatuses.Deleted);
        this.selectedMemberStatusOptions.set([...this.activeFilter.statuses]);
      }
    }
  }

  public override get activeFilter(): MembersFilter {
    return this.localFilter() ?? this.userSessionService.membersFilter;
  }

  protected override getDefaultFilter(): MembersFilter {
    const factory = this.defaultFilterFactory();
    return factory ? factory() : ListConstants.MEMBERS_FILTER_DEFAULT();
  }

  protected override get preservedFilterKeys(): (keyof MembersFilter)[] {
    return ['organizationIds', 'memberIds'];
  }

  protected override get dropdownSelector(): string {
    return '#member-search-filter-dropdown';
  }

  protected override applyAuthGatedFields(): void {
    const authState = this.authorizationStateService.authorizationState();
    this.applyDeletedMemberAccess(authState ?? null);
  }

  protected override syncStateFromFilter(): void {
    this.selectedMemberStatusOptions.set([...(this.activeFilter.statuses || [])]);
    this.selectedRoleOptions.set(this.roleNamesFromIds(this.activeFilter.roleIds));
  }

  public ngOnInit(): void {
    this.normalizeLegacyFilter();
    this.selectedMemberStatusOptions.set(this.activeFilter.statuses ?? this.getDefaultFilter().statuses ?? []);
    this.selectedRoleOptions.set(this.roleNamesFromIds(this.activeFilter.roleIds));
    this.onApply();
  }

  // Strips pre-DEV-2505 leftovers from a filter hydrated from localStorage: status
  // values no longer in MemberStatuses (e.g. 'Inactive' → renamed to 'Deactivated')
  // and the removed invitationStatuses key. Without this, a returning user's persisted
  // filter would silently produce empty results and continue emitting a dead key.
  private normalizeLegacyFilter(): void {
    const filter = this.activeFilter;
    if (filter.statuses?.length) {
      const validStatuses = new Set<string>(Object.values(MemberStatuses));
      filter.statuses = filter.statuses.filter(s => validStatuses.has(s));
    }
    if (filter.roleIds?.length) {
      filter.roleIds = filter.roleIds.filter(id => id in RoleIds);
    }
    delete (filter as any).invitationStatuses;
  }

  private roleNamesFromIds(roleIds?: number[]): (keyof typeof RoleIds)[] {
    return (roleIds ?? [])
      .map(id => RoleIds[id] as keyof typeof RoleIds)
      .filter(name => name !== undefined);
  }

  // Persist to the provided key, or to the shared session filter only when operating on it (no localFilter).
  protected override saveToStorage(): void {
    const storageKey = this.localStorageKey();
    if (storageKey) {
      localStorage.setItem(storageKey, JSON.stringify(this.activeFilter));
    } else if (!this.localFilter()) {
      this.userSessionService.saveToLocalStorage(LocalStorageKeyConstants.MEMBERS_FILTER(), this.activeFilter);
    }
  }

  public onStatusChange(event: any) {
    this.activeFilter.statuses = event;
    this.saveToStorage();
  }

  public onRoleChange(selected: (keyof typeof RoleIds)[]): void {
    this.activeFilter.roleIds = selected.map(name => RoleIds[name]);
    this.saveToStorage();
  }

  public override setFilterCount(): void {
    this.filterCount = (this.activeFilter.firstNames?.length || 0) +
      (this.activeFilter.lastNames?.length || 0) +
      (this.activeFilter.emailAddresses?.length || 0) +
      (this.activeFilter.statuses?.length || 0) +
      (this.activeFilter.roleIds?.length || 0);
  }

  public addFirstName(): void {
    if (this.firstNameInput && !(this.firstNameInput.trim() === '')) {
      this.activeFilter.firstNames?.push(this.firstNameInput);
      this.saveToStorage();
    }
    this.firstNameInput = '';
  }

  public removeFirstName(index: number): void {
    this.activeFilter.firstNames?.splice(index, 1);
    this.saveToStorage();
  }

  public addLastName(): void {
    if (this.lastNameInput && !(this.lastNameInput.trim() === '')) {
      this.activeFilter.lastNames?.push(this.lastNameInput);
      this.saveToStorage();
    }
    this.lastNameInput = '';
  }

  public removeLastName(index: number): void {
    this.activeFilter.lastNames?.splice(index, 1);
    this.saveToStorage();
  }

  public addEmail(): void {
    if (this.emailInput && !(this.emailInput.trim() === '')) {
      this.activeFilter.emailAddresses?.push(this.emailInput);
      this.saveToStorage();
    }
    this.emailInput = '';
  }

  public removeEmail(index: number): void {
    this.activeFilter.emailAddresses?.splice(index, 1);
    this.saveToStorage();
  }

  public addPhoneNumber(): void {
    if (this.phoneInput && !(this.phoneInput.trim() === '')) {
      this.activeFilter.phoneNumbers?.push(this.phoneInput);
      this.saveToStorage();
    }
    this.phoneInput = '';
  }

  public removePhoneNumber(index: number): void {
    this.activeFilter.phoneNumbers?.splice(index, 1);
    this.saveToStorage();
  }

  public removeStatus(index: number): void {
    this.activeFilter.statuses?.splice(index, 1);
    this.saveToStorage();
  }
}
